import { Map } from 'immutable'
import _parseInt from 'lodash/parseInt'
import _get from 'lodash/get'
import _includes from 'lodash/includes'
import { SET_PAGE_READY } from 'services/pages/redux/actions'
import { SET_LOCATION_DATA } from 'services/location/redux/actions'
import {
  GET_CS_PARAMS,
  REMOVE_CS_PARAM,
  CREATE_CS_PARAM,
  UPDATE_CS_PARAM,
  PROCESS_REINDEX,
  setCSParam as setCSParamAction,
  getCSParams as getCSParamAction,
  setCSParamOffsetLimit as setCSParamOffsetLimitAction,
  setError,
} from './actions'
import {
  storeKey as storeKeyConstant,
  getCSParamsList,
  deleteCSParam,
  upsertCSParam,
  reindex,
} from '..'

const log = console

/**
 * Format errors for the display alert
 */
function formatAlert (error, message) {
  let errBody = _get(error, ['response', 'body', 'errors', 0])
  if (!errBody) {
    errBody = {
      status: _get(error, ['response', 'status'], 0),
      title: _get(error, ['response', 'body', 'name'], 'No name'),
      detail: _get(error, ['response', 'body', 'message'], 'No message'),
      meta: '',
    }
  }
  return `${message}\n     status= ${errBody.status}\n     details: ${errBody.title}, ${errBody.detail}, ${errBody.meta}`
}

/**
 * Handle the page ready event at this point we have auth and config
 */
async function pageReadyHandler ({ store, action }) {
  const { payload } = action
  if (payload === true) {
    store.dispatch(getCSParamAction({ storeKey: storeKeyConstant }))
  }
}

/**
 * Handle getting Redirects
 */
async function getCSParamsHandler ({ store, action }) {
  const {
    config, auth, csParams, location,
  } = store.getState()
  const {
    storeKey,
    offset = _parseInt(location.getIn(['data', 'query', 'offset'], 0)),
    limit = _parseInt(location.getIn(['data', 'query', 'limit'], 100)),
    contentType = location.getIn('data', 'query', 'contentType'),
    searchColumn = location.get('data', 'query', 'searchColumn'),
  } = action.payload
  if (!storeKey) {
    throw new Error('StoreKey required to get content search params')
  }
  const processing = csParams.getIn([storeKey, 'processing'])
  const query = location.getIn(['data', 'query'])
  const hasChanged = (csParams.getIn([storeKey, 'offset']) !== offset)
    || (csParams.getIn([storeKey, 'contentType']) !== contentType)
    || (csParams.getIn([storeKey, 'searchColumn']) !== searchColumn)
  if (processing || !hasChanged) {
    return
  }
  try {
    store.dispatch(setCSParamOffsetLimitAction({ storeKey, offset, limit }))
    const csParamsList = await getCSParamsList({
      auth,
      server: config.getIn(['data', 'servers', 'brooklyn']),
      contentType: query.get('contentType'),
      searchColumn: query.get('searchColumn'),
      offset,
      limit,
    })
    const csParamData = csParamsList.get('searchParams')
    store.dispatch(setCSParamAction({
      storeKey,
      data: csParamData,
      total: csParamsList.get('total'),
    }))
    // console.log( `Fetched list: ${JSON.stringify(csParamsList)}`)
  } catch (error) {
    log.error(error, 'Error fetching content search parameters list')
    store.dispatch(setError(formatAlert(error, 'Error fetching content search parameters list')))
  }
}

/**
 * Handle the remove request.  Note, we also need to refetch the list based to refresh the page
 */
async function removeCSParamHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { payload = {} } = action
  const { id } = payload
  const server = config.getIn(['data', 'servers', 'brooklyn'])
  try {
    await deleteCSParam({ server, id, auth })
    await getCSParamsHandler({ store, action })
  } catch (error) {
    log.error(error, 'Error removing content search param.')
    store.dispatch(setError(formatAlert(error, 'Error removing content search param')))
  }
}

/**
 * Handle the create/update requests. Request without and id is a create
 *   Note, we also need to refetch the list based to refresh the page
 */
async function upsertCSParamHandler ({ store, action }) {
  const { config, auth } = store.getState()
  const { payload = {} } = action
  const { data } = payload
  const server = config.getIn(['data', 'servers', 'brooklyn'])
  try {
    await upsertCSParam({ server, data, auth })
    await getCSParamsHandler({ store, action })
  } catch (error) {
    log.error(error, 'Error upserting content search parameter.')
    store.dispatch(setError(formatAlert(error, 'Could not upsert content search parameter')))
  }
}

/**
 * Handle the request to re-index the content search table on brooklyn
 *   using current set of search params
 */
async function reindexHandler ({ store }) {
  const { config, auth } = store.getState()
  // const { payload = {} } = action
  // const { data } = payload
  const server = config.getIn(['data', 'servers', 'brooklyn'])
  try {
    await reindex({ server, auth })
  } catch (error) {
    const errorStatus = _get(error, ['response', 'status'])
    if (errorStatus === 429) {
      // if here, reporting that reindex currently in progress
      store.dispatch(setError(formatAlert(error, 'Re-index currently in progress, please try agian later')))
    } else {
      log.error(error, 'Error reindexing content search table.')
      store.dispatch(setError(formatAlert(error, 'Could not reindex search table')))
    }
  }
}

function isContentSearchPage (location) {
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map')
  }
  return _includes(
    ['/content/search', '/content-search'],
    location.getIn(['data', 'pathname']),
  )
}

export default (store) => (next) => (action) => {
  const { type } = action
  switch (type) {
    case SET_PAGE_READY: {
      const { location = Map() } = store.getState()
      if (isContentSearchPage(location)) {
        pageReadyHandler({ store, action })
      }
      break
    }
    case GET_CS_PARAMS:
      getCSParamsHandler({ store, action })
      break
    case REMOVE_CS_PARAM:
      removeCSParamHandler({ store, action })
      break
    case CREATE_CS_PARAM:
    case UPDATE_CS_PARAM:
      upsertCSParamHandler({ store, action })
      break
    case PROCESS_REINDEX:
      reindexHandler({ store, action })
      break
    default:
      break
  }
  next(action)
  switch (type) {
    case SET_LOCATION_DATA: {
      const { location = Map(), page = Map() } = store.getState()
      if (isContentSearchPage(location) && page.get('ready')) {
        store.dispatch(getCSParamAction({ storeKey: storeKeyConstant }))
      }
      break
    }
    default:
      break
  }
}
